import { Modal } from './ui';
import { CallRecordingPlayer } from './CallRecording';
import { useAttachmentUrl } from './AttachmentPreview';

/** مشغّلُ فيديو لمرفقٍ داخليّ (تسجيلُ اجتماعِ Jitsi) — يُجلب بثًّا كـ blob. */
function AttachmentVideoPlayer({ id }: { id: string }) {
  const { url, loading, error } = useAttachmentUrl(id, true);
  if (loading) return <p className="small">جارٍ تحميلُ التسجيل…</p>;
  if (error || !url) return <p className="small">تعذّر تحميلُ التسجيل.</p>;
  /* eslint-disable-next-line jsx-a11y/media-has-caption -- تسجيل اجتماع داخلي لا مادة منشورة. */
  return <video className="meet-video" src={url} controls autoPlay />;
}

/**
 * تشغيل تسجيل الاجتماع — تمييز الرابط ومشغّله، مشتركةً.
 *
 * تفاصيل الاجتماع وصفّه في الملف وخطّ «ما جرى» ثلاثتها تعرض التسجيل،
 * فالتمييز والمشغّل هنا مرةً واحدة: الملف المباشر يعزفه وسم <video>،
 * ودرايف ويوتيوب ولووم عبر صفحات تضمينها الرسمية في إطار — أما
 * تسجيلات زوم السحابية فمنصّتها تمنع التضمين، فتُفتح عندها بصراحة
 * لا بمشغّلٍ يدّعي.
 */
export function recordingKind(
  url: string,
): { kind: 'file' | 'embed' | 'external'; src: string } | null {
  if (/\.(mp4|webm|ogv|ogg|m4v|mov)(\?|#|$)/i.test(url)) return { kind: 'file', src: url };

  const drive = url.match(/drive\.google\.com\/file\/d\/([^/?#]+)/);
  if (drive) return { kind: 'embed', src: `https://drive.google.com/file/d/${drive[1]}/preview` };

  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (yt) return { kind: 'embed', src: `https://www.youtube.com/embed/${yt[1]}` };

  const loom = url.match(/loom\.com\/share\/([a-f0-9]+)/i);
  if (loom) return { kind: 'embed', src: `https://www.loom.com/embed/${loom[1]}` };

  if (/zoom\.us\/rec/i.test(url)) return { kind: 'external', src: url };

  return null;
}

/**
 * أهو مرفقٌ في النظام أم رابطٌ عند منصّة؟
 *
 * تسجيلُ الاجتماع رابطٌ عند مصدره (درايف أو يوتيوب أو زووم)، وتسجيلُ
 * المكالمة مرفقٌ عندنا يُشار إليه بمعرّفه. والعمودُ واحدٌ يحملهما، فيُفرَّق
 * بالشكل: ما ليس فيه بروتوكولٌ ولا مسارٌ ولا امتدادٌ فهو معرّفُ مرفق.
 */
export function isAttachmentId(v: string): boolean {
  return /^[a-z0-9]{20,32}$/i.test(v.trim());
}

/** اسمُ التسجيل كما يُقرأ — مكالمةً عندنا أو اجتماعًا عند منصّة. */
export function recordingLabel(url: string): string {
  return isAttachmentId(url) ? "تسجيل المكالمة" : "تسجيل الاجتماع";
}

/** مشغّل التسجيل — مرفقًا عندنا، أو ملفًّا مباشرًا، أو تضمينًا عند منصّة. */
export function RecordingPlayerModal({
  url,
  onClose,
  video = false,
}: {
  url: string;
  onClose: () => void;
  /** المرفقُ الداخليُّ فيديو (تسجيلُ اجتماع) لا صوتٌ (تسجيلُ مكالمة). */
  video?: boolean;
}) {
  const v = recordingKind(url);

  /* تسجيلُ اجتماعِ Jitsi مرفقٌ داخليٌّ فيديو — يُشغَّل بوسمِ <video>. */
  if (!v && isAttachmentId(url) && video) {
    return (
      <Modal title="تسجيل الاجتماع" onClose={onClose} wide>
        <AttachmentVideoPlayer id={url} />
      </Modal>
    );
  }

  /*
   * المرفقُ يُشغَّل بمشغّل المكالمات.
   *
   * كان هذا المشغّلُ يُبنى للاجتماعات فيقرأ الروابط وحدها، وسجلُّ التعامل
   * في ملفّ العميل يستعمله لكلّ تسجيل — فمكالمةٌ تسجيلُها مرفقٌ عندنا كانت
   * تُقرأ «رابطٌ لا يُشغَّل داخل النظام» وهي محفوظةٌ فيه. فالمرفقُ يُعطى
   * مشغّلَه: يُجلب الصوتُ بمعرّفه ويُسمع في مكانه.
   */
  if (!v && isAttachmentId(url)) {
    return (
      <Modal title="تسجيل المكالمة" onClose={onClose}>
        <CallRecordingPlayer id={url} autoArm />
      </Modal>
    );
  }

  return (
    <Modal title="تسجيل الاجتماع" onClose={onClose} wide>
      {v?.kind === 'file' ? (
        /* eslint-disable-next-line jsx-a11y/media-has-caption -- تسجيل اجتماع داخلي لا مادة منشورة. */
        <video className="meet-video" src={v.src} controls />
      ) : v?.kind === 'embed' ? (
        <iframe
          className="meet-video"
          src={v.src}
          title="تسجيل الاجتماع"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      ) : (
        /* رابطٌ لا نعرف تشغيله داخلًا — يُفتح عند مصدره بصراحة. */
        <p className="small">
          هذا الرابط لا يُشغَّل داخل النظام —{' '}
          <a href={url} target="_blank" rel="noopener noreferrer" className="link">
            افتحه عند مصدره
          </a>
          .
        </p>
      )}
      {v && v.kind !== 'external' && (
        <p className="tiny mute mt-2">
          <a href={url} target="_blank" rel="noopener noreferrer" className="link">
            افتح الرابط الأصلي
          </a>{' '}
          — إن تعثّر التشغيل هنا فقيود المصدر لا النظام.
        </p>
      )}
    </Modal>
  );
}
